<template>
    <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
        <page-header :subtitle="pageSubtitle" :title="pageTitle" />

        <content-box>
            <template #header>
                <import-steps :path-prefix="pathPrefix" />
            </template>
            <template #body>
                <nio-table
                    :columns="columns"
                    :rows="formattedPreviewRows"
                    ref="body"
                >
                    <template v-slot:[`column-${column.name}`]="{ row, index }" v-for="column in columns">
                        <import-preview-list-item
                            :column="column.name"
                            :import-type="importType"
                            :index="index"
                            :placeholders="displayedPlaceholdersColumns"
                            :row="row"
                            :truncate="column.truncate"
                            :key="column.name"
                        />
                    </template>
                </nio-table>
            </template>
            <template #footer>
                <div class="flex-1 flex items-center justify-start p-4">
                    <p class="flex items-center" v-if="formattedPreviewRows.length > 100">
                        <nio-icon class="mr-2 text-lg" name="regular/information-circle" />
                        {{ $t('imports.preview.firstHundredOnly') }}
                    </p>
                </div>
                <div class="flex-1 flex items-center justify-end p-4 space-x-2">
                    <nio-button icon="regular/arrow-left" @click="goBack">
                        {{ $t('global.general.back') }}
                    </nio-button>

                    <nio-button
                        color="blue"
                        :disabled="importDisabled"
                        icon="regular/check-double-1"
                        :loading="importing"
                        @click="openImport = true"
                    >
                        {{ $t('imports.preview.btnImport') }}
                    </nio-button>
                </div>
            </template>
        </content-box>

        <confirm-modal
            :opened.sync="openImport"
            :title="$t('imports.preview.importModal.header')"
            @confirm="importFile"
        >
            <p slot="message">
                {{ $t('imports.preview.importModal.message') }}
            </p>
        </confirm-modal>

        <confirm-modal
            :opened.sync="openLeave"
            :title="$t('imports.confirmLeavingModal.header')"
            @confirm="resumeLeavePage"
        >
            <p slot="message">
                {{ $t('imports.confirmLeavingModal.message') }}
            </p>
        </confirm-modal>
    </div>
</template>

<script>
    import { mapState, mapMutations } from 'vuex';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ImportPreviewListItem from '@/views/Imports/ImportPreviewListItem.vue';
    import ImportSteps from '@/components/ImportSteps.vue';
    import PageHeader from '@/components/PageHeader.vue';

    export default {
        components: {
            ConfirmModal,
            ContentBox,
            ImportPreviewListItem,
            ImportSteps,
            PageHeader,
        },

        props: {
            importType: {
                type: String,
                required: true,
            },
            pathPrefix: {
                type: String,
                required: true,
            },
            pageSubtitle: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                importing: false,
                nextPath: '',
                preventQuitting: true,
                openImport: false,
                openLeave: false,
                columnsTreshold: 3,
            };
        },

        computed: {
            ...mapState('appointment', [
                'matchedColumns',
                'previewRows',
            ]),

            pageTitle() {
                return this.$t(`imports.pageTitle.${this.pathPrefix}`);
            },

            importDisabled() {
                const dateMatched = this.matchedColumns.some(column => ['date', 'datetime'].includes(column.value));
                const phoneMatched = this.matchedColumns.some(column => column.value == 'phone');

                return this.importType == 'appointment' && !dateMatched ||
                    !phoneMatched ||
                    empty(this.formattedPreviewRows);
            },

            formattedPreviewRows() {
                return this.previewRows.map(row => {
                    const name = this.getName(row);
                    const phone = this.getPhone(row);
                    let date = this.getDateTime(row);
                    const email = this.getEmail(row);
                    const appointmentNotes = this.getAppointmentNotes(row);
                    const contactNotes = this.getContactNotes(row);
                    const communicationPreference = this.getCommunicationPreference(row);
                    let invalid = '';

                    // Validate
                    if (empty(name)) {
                        invalid = 'name';
                    }

                    if (empty(phone)) {
                        invalid = 'phone';
                    }

                    if (this.importType == 'appointment') {
                        if (empty(date) || date == 'invalid') {
                            invalid = 'date';
                        }

                        if (date == 'invalid') {
                            date = this.$t('imports.preview.invalidDate');
                        }
                    }

                    const response = {
                        name,
                        phone,
                        date,
                        email,
                        appointmentNotes,
                        contactNotes,
                        invalid,
                        communicationPreference,
                    };

                    for (const column of this.filteredNewPlaceholders) {
                        response[column.name] = row[column.name];
                    }

                    return response;
                });
            },

            showEmail() {
                const emailMatched = this.matchedColumns.find(c => c.value == 'email');

                return !empty(emailMatched);
            },

            showContactNotes() {
                const contactNotesMatched = this.matchedColumns.find(c => c.value == 'contactNotes');

                return !empty(contactNotesMatched);
            },

            showAppointmentNotes() {
                const appointmentNotesMatched = this.matchedColumns.find(c => c.value == 'appointmentNotes');

                return !empty(appointmentNotesMatched);
            },

            filteredNewPlaceholders() {
                return this.matchedColumns.filter(column => column.value == 'new');
            },

            displayedPlaceholdersColumns() {
                return this.filteredNewPlaceholders.slice(0, this.columnsTreshold);
            },

            hiddenColumnsCount() {
                return this.filteredNewPlaceholders.length - this.columnsTreshold;
            },

            columns() {
                return [
                    {
                        name: 'number',
                        icon: 'regular/hash',
                        className: 'w-12',
                        align: 'center',
                    },
                    {
                        name: 'name',
                        label: this.$t('imports.preview.name'),
                        icon: 'regular/single-neutral',
                        className: 'flex-3',
                    },
                    {
                        name: 'date',
                        label: this.$t('imports.preview.date'),
                        icon: 'regular/calendar-3',
                        className: 'flex-2',
                    },
                    {
                        name: 'phone',
                        label: this.$t('imports.preview.phone'),
                        icon: 'regular/phone',
                        className: 'flex-2',
                    },
                    {
                        name: 'email',
                        label: this.$t('imports.preview.email'),
                        icon: 'regular/envelope',
                        className: 'flex-2',
                    },
                    {
                        name: 'appointmentNotes',
                        label: this.$t('imports.preview.appointmentNotes'),
                        icon: 'regular/notes-paper-text',
                        className: 'flex-2',
                    },
                    {
                        name: 'contactNotes',
                        label: this.$t('imports.preview.contactNotes'),
                        icon: 'regular/notes-paper-text',
                        className: 'flex-2',
                    },
                    {
                        name: 'communicationPreference',
                        label: this.$t('imports.preview.communicationPreference'),
                        icon: 'regular/messages-bubble-heart',
                        className: 'flex-2',
                    },
                    ...this.displayedPlaceholdersColumns.map((placeholderColumn, index) => {
                        return {
                            name: `placeholder${index}`,
                            label: placeholderColumn.placeholder,
                            icon: 'regular/add',
                            className: 'flex-2',
                        };
                    }),
                    {
                        name: 'status',
                        label: this.filteredNewPlaceholders.length > this.columnsTreshold
                            ? this.$tc('imports.preview.moreColumns', this.hiddenColumnsCount, { number: this.hiddenColumnsCount })
                            : this.$t('imports.preview.status'),
                        icon: this.filteredNewPlaceholders.length > this.columnsTreshold
                            ? ''
                            : 'regular/question-circle',
                        className: 'flex-1',
                    },
                ].filter(column => {
                    switch (column.name) {
                        case 'date':
                            return this.importType == 'appointment';
                        case 'email':
                            return this.showEmail;
                        case 'appointmentNotes':
                            return this.showAppointmentNotes;
                        case 'contactNotes':
                            return this.showContactNotes;
                        case 'moreColumns':
                            return this.filteredNewPlaceholders.length > this.columnsTreshold;
                        default:
                            return true;
                    }
                });
            },
        },

        methods: {
            ...mapMutations('campaign', [
                'clearSelectedCampaign',
            ]),

            parseDate(date) {
                if (empty(date)) {
                    return '';
                }

                let momentDate;
                const dates = [];

                date = date.toString();

                if (date.match(/^(\d{2})-(\d{2})-(\d{2})$/)) {
                    dates.push(moment(date, 'YY-MM-DD', true));
                    dates.push(moment(date, 'DD-MM-YY', true));
                    dates.push(moment(date, 'MM-DD-YY', true));
                } else if (date.match(/^(\d{2})\/(\d{2})\/(\d{2})$/)) {
                    dates.push(moment(date, 'YY/MM/DD', true));
                    dates.push(moment(date, 'DD/MM/YY', true));
                    dates.push(moment(date, 'MM/DD/YY', true));
                } else if (date.match(/^(\d)-(\d)-(\d{4})$/)) {
                    dates.push(moment(date, 'D-M-YYYY', true));
                    dates.push(moment(date, 'M-D-YYYY', true));
                } else if (date.match(/^(\d)\/(\d)\/(\d{4})$/)) {
                    dates.push(moment(date, 'D/M/YYYY', true));
                    dates.push(moment(date, 'M/D/YYYY', true));
                } else if (date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
                    dates.push(moment(date, 'DD/MM/YYYY', true));
                    dates.push(moment(date, 'MM/DD/YYYY', true));
                } else {
                    dates.push(moment(date));
                }

                // Filter invalid dates
                const validDates = dates.filter(tmpDate => tmpDate.isValid());

                // Choose appropriate date
                if (validDates.length == 0) {
                    momentDate = dates[0];
                } else {
                    momentDate = null;

                    for (const tmpDate of validDates) {
                        const current = Math.abs(moment().diff(tmpDate));
                        const previous = Math.abs(moment().diff(momentDate));

                        if (!momentDate || current < previous) {
                            momentDate = tmpDate;
                        }
                    }
                }

                return momentDate.format('YYYY-MM-DD');
            },

            parseTime(time) {
                time = time.toString().trim();

                if (!time) {
                    return '';
                }

                time = this.convertTo24h(time);

                // Remove seconds
                if (time.match(/^(\d{2}):(\d{2}):(\d{2})$/)) {
                    time = time.slice(0, -3);
                }

                // Add leading zero
                if (time.match(/^(\d):(\d{2})$/)) {
                    time = `0${time}`;
                }

                return time;
            },

            convertTo24h(time) {
                const possibleFormats = [
                    'h:mm a',
                    'hh:mm a',
                    'h:mma',
                    'hh:mma',
                    'h:mm:ss a',
                    'hh:mm:ss a',
                    'h:mm:ssa',
                    'hh:mm:ssa',
                ];

                const format = possibleFormats.find(format => {
                    return moment(time.toLowerCase(), format, true).isValid();
                });

                return format ? moment(time, format).format('HH:mm') : time;
            },

            getName(row) {
                let fullName = '';
                let firstName = '';
                let lastName = '';

                for (const column of this.matchedColumns) {
                    // Full name
                    if (column.value == 'fullName') {
                        fullName = row[column.name] || '';
                    }

                    // Split name
                    if (column.value == 'firstName') {
                        firstName = row[column.name] || '';
                    } else if (column.value == 'lastName') {
                        lastName = row[column.name] || '';
                    }
                }

                // Build from first and last
                if (empty(fullName)) {
                    fullName = `${firstName} ${lastName}`;
                }

                // Swap position if apostrophy
                if (fullName.indexOf(',') !== -1) {
                    const parts = fullName.split(',');

                    // Trim
                    parts.map(part => part.trim());

                    // Reverse
                    parts.reverse();

                    fullName = parts.join(' ');
                }

                return fullName.trim();
            },

            getPhone(row) {
                for (const column of this.matchedColumns) {
                    if (column.value == 'phone') {
                        return this.formatPhone(row[column.name]);
                    }
                }

                return '';
            },

            getDateTime(row) {
                let date = '';
                let time = '';

                for (const column of this.matchedColumns) {
                    const value = row[column.name] || '';

                    if (column.value == 'datetime') {
                        date = value.substr(0, value.indexOf(' '));
                        time = value.substr(value.indexOf(' ') + 1);
                    } else if (column.value == 'date') {
                        date = value;
                    } else if (column.value == 'time') {
                        time = value;
                    }
                }

                const datetime = `${this.parseDate(date)} ${this.parseTime(time)}`.trim();

                // If not valid
                if (!datetime) {
                    return null;
                }

                if (!moment(datetime, moment.ISO_8601, true).isValid()) {
                    return 'invalid';
                }

                if (this.$i18n.locale == 'en') {
                    return moment(datetime).format('DD MMM YYYY hh:mm A');
                }

                return moment(datetime).format('DD MMM YYYY HH:mm');
            },

            getEmail(row) {
                for (const column of this.matchedColumns) {
                    if (column.value == 'email') {
                        return row[column.name];
                    }
                }

                return '';
            },

            getAppointmentNotes(row) {
                for (const column of this.matchedColumns) {
                    if (column.value == 'appointmentNotes') {
                        return row[column.name];
                    }
                }

                return '';
            },

            getContactNotes(row) {
                for (const column of this.matchedColumns) {
                    if (column.value == 'contactNotes') {
                        return row[column.name];
                    }
                }

                return '';
            },

            getCommunicationPreference(row) {
                for (const column of this.matchedColumns) {
                    if (column.value == 'communicationPreference') {
                        const value = row[column.name] || '';
                        const possibleValues = [
                            { name: this.$t('imports.preview.communicationPreferences.sms'), values: ['sms'] },
                            { name: this.$t('imports.preview.communicationPreferences.email'), values: ['email', 'courriel'] },
                            { name: this.$t('imports.preview.communicationPreferences.phone'), values: ['phone', 'call', 'telephone', 'appel'] },
                        ];

                        const match = possibleValues.find(possibleValue => {
                            return !!value && possibleValue.values.some(v => value.localeCompare(v, 'en', { sensitivity: 'base' }) === 0);
                        });

                        return match?.name || '';
                    }
                }

                return '';
            },

            goBack() {
                this.preventQuitting = false;

                this.$nextTick(() => {
                    this.$emit('back');
                });
            },

            async importFile() {
                this.importing = true;

                try {
                    await this.$axios.post(`imports/${this.$route.params.id}/process`, {
                        matchedColumns: this.matchedColumns,
                    });

                    this.preventQuitting = false;

                    this.$nextTick(() => {
                        this.$notify.show({
                            type: 'success',
                            message: this.$t('imports.preview.successMessage'),
                        });

                        this.$emit('complete');
                    });

                    this.importing = false;
                } catch (error) {
                    this.importing = false;

                    if (empty(error) || empty(error.response)) {
                        this.$notify.show({
                            type: 'error',
                            message: this.$t('global.errors.unknown'),
                        });

                        throw error;
                    }

                    // Import deleted, redirect
                    if (error.response.status == 404) {
                        this.$emit('complete');
                    }
                }
            },

            resumeLeavePage() {
                this.preventQuitting = false;

                if (this.importType == 'campaign') {
                    this.clearSelectedCampaign();
                }

                this.$router.push(this.nextPath);
            },

            onBeforeRouteEnter() {
                // Reset values
                this.importing = false;
                this.nextPath = '';
                this.preventQuitting = true;
                this.openLeave = false;

                if (empty(this.matchedColumns)) {
                    const payload = {
                        name: `${this.pathPrefix}.import.match`,
                        params: {
                            id: this.$route.params.id,
                        },
                    };

                    if (this.importType == 'campaign') {
                        payload.params.campaignId = this.$route.params.campaignId;
                    }

                    this.$router.replace(payload);
                }
            },

            // Confirmation modal
            onBeforeRouteLeave(to, next) {
                if (this.preventQuitting &&
                    to.name != `${this.pathPrefix}.import.preview` &&
                    to.name != `${this.pathPrefix}.import.match`
                ) {
                    this.nextPath = to.path;
                    this.openLeave = true;
                } else {
                    this.preventQuitting = true;
                    next();
                }
            },
        },
    };
</script>
